import { CollapsibleFilters } from '../../../components/common/CollapsibleFilters';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  Sheet,
  Button,
  Select,
  Option,
  Input,
  FormControl,
  FormLabel,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
} from '@mui/joy';
import {
  ArrowsClockwise as Refresh,
  DownloadSimple as Download,
  CaretDown as ExpandMore,
  MagnifyingGlass as Search,
  Wallet as AccountBalanceWallet,
  CheckCircle,
  XCircle as Cancel,
  Warning,
  CaretLineLeft as FirstPage,
  CaretLineRight as LastPage,
  CaretLeft as NavigateBefore,
  CaretRight as NavigateNext,
} from '@phosphor-icons/react';
import { apiAccountingClient } from '../../../lib/apiAccounting';
import type { JournalEntry } from '../../../types/accounting';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface JournalEntriesData {
  results: JournalEntry[];
  count: number;
  next: string | null;
  previous: string | null;
}

export const JournalEntriesReport: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<JournalEntriesData | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Filters
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [entryType, setEntryType] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        start_date: startDate,
        end_date: endDate,
        page,
        page_size: pageSize === -1 ? 1000 : pageSize,
      };

      if (entryType) params.entry_type = entryType;
      if (status) params.status = status;
      if (searchTerm) params.search = searchTerm;

      const response = await apiAccountingClient.getJournalEntries(params);
      setData(response);
    } catch (err) {
      setError('Failed to load journal entries report');
      console.error('Report error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [startDate, endDate, entryType, status, searchTerm, page, pageSize]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, entryType, status, searchTerm, pageSize]);

  const handleToggleRow = (id: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleExport = async () => {
    if (!data) return;

    // Create CSV content
    const headers = [
      'Entry Number',
      'Date',
      'Type',
      'Status',
      'Description',
      'Reference',
      'Account',
      'Debit',
      'Credit',
      'Created By'
    ];

    const rows: string[][] = [];

    data.results.forEach(entry => {
      // Add main entry row
      rows.push([
        entry.entry_number,
        formatDate(entry.entry_date),
        entry.entry_type,
        entry.status,
        entry.memo || entry.description || '',
        entry.reference_number || '',
        '',
        formatCurrency(entry.total_debits || 0),
        formatCurrency(entry.total_credits || 0),
        entry.created_by_name || ''
      ]);

      // Add line item rows
      if (entry.lines) {
        entry.lines.forEach(line => {
          rows.push([
            '',
            '',
            '',
            '',
            line.description || '',
            '',
            `${line.account_number || ''} - ${line.account_name || ''}`,
            line.debit_amount > 0 ? formatCurrency(line.debit_amount) : '',
            line.credit_amount > 0 ? formatCurrency(line.credit_amount) : '',
            ''
          ]);
        });
      }

      // Add blank row between entries
      rows.push(['', '', '', '', '', '', '', '', '', '']);
    });

    // Create CSV
    const csvContent = [
      'Journal Entries Report',
      `Period: ${formatDate(startDate)} to ${formatDate(endDate)}`,
      '',
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `journal-entries-${startDate}-to-${endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Calculate summary statistics
  const summary = data ? {
    totalEntries: data.count,
    totalDebits: data.results.reduce((sum, entry) => sum + (entry.total_debits || 0), 0),
    totalCredits: data.results.reduce((sum, entry) => sum + (entry.total_credits || 0), 0),
    postedCount: data.results.filter(e => e.status === 'POSTED').length,
    draftCount: data.results.filter(e => e.status === 'DRAFT').length,
    voidedCount: data.results.filter(e => e.status === 'VOIDED').length,
  } : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, mb: 3 }}>
        <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>Journal Entries Report</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={fetchReport}
            disabled={loading}
            variant="outlined"
          >
            <Refresh />
          </IconButton>
          <Button
            startDecorator={<Download />}
            onClick={handleExport}
            disabled={!data || data.results.length === 0}
            variant="outlined"
            color="primary"
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <CollapsibleFilters><Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid xs={12} md={3}>
              <FormControl>
                <FormLabel>Start Date</FormLabel>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid #D1D5DB',
                    fontSize: '14px'
                  }}
                />
              </FormControl>
            </Grid>
            <Grid xs={12} md={3}>
              <FormControl>
                <FormLabel>End Date</FormLabel>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid #D1D5DB',
                    fontSize: '14px'
                  }}
                />
              </FormControl>
            </Grid>
            <Grid xs={12} md={2}>
              <FormControl>
                <FormLabel>Type</FormLabel>
                <Select
                  value={entryType}
                  onChange={(_, value) => setEntryType(value as string)}
                  placeholder="All Types"
                >
                  <Option value="">All Types</Option>
                  <Option value="STANDARD">Standard</Option>
                  <Option value="ADJUSTING">Adjusting</Option>
                  <Option value="REVERSING">Reversing</Option>
                  <Option value="CLOSING">Closing</Option>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} md={2}>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={status}
                  onChange={(_, value) => setStatus(value as string)}
                  placeholder="All Statuses"
                >
                  <Option value="">All Statuses</Option>
                  <Option value="DRAFT">Draft</Option>
                  <Option value="POSTED">Posted</Option>
                  <Option value="VOIDED">Voided</Option>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} md={2}>
              <FormControl>
                <FormLabel>Search</FormLabel>
                <Input
                  placeholder="Entry # or description"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  startDecorator={<Search />}
                />
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card></CollapsibleFilters>

      {error && (
        <Alert color="danger" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid xs={12} md={2}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccountBalanceWallet size={16} style={{ marginRight: 8 }} />
                  <Typography level="body-sm">Total Entries</Typography>
                </Box>
                <Typography level="h3">{summary.totalEntries}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} md={2}>
            <Card>
              <CardContent>
                <Typography level="body-sm" sx={{ mb: 1 }}>Total Debits</Typography>
                <Typography level="h4">{formatCurrency(summary.totalDebits)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} md={2}>
            <Card>
              <CardContent>
                <Typography level="body-sm" sx={{ mb: 1 }}>Total Credits</Typography>
                <Typography level="h4">{formatCurrency(summary.totalCredits)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} md={2}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircle size={16} color="var(--joy-palette-success-500)" style={{ marginRight: 8 }} />
                  <Typography level="body-sm">Posted</Typography>
                </Box>
                <Typography level="h3">{summary.postedCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} md={2}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Warning size={16} color="var(--joy-palette-warning-500)" style={{ marginRight: 8 }} />
                  <Typography level="body-sm">Draft</Typography>
                </Box>
                <Typography level="h3">{summary.draftCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid xs={12} md={2}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Cancel size={16} color="var(--joy-palette-danger-500)" style={{ marginRight: 8 }} />
                  <Typography level="body-sm">Voided</Typography>
                </Box>
                <Typography level="h3">{summary.voidedCount}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Journal Entries Table */}
      {data && !loading && (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Sheet
              variant="outlined"
              sx={{
                overflow: 'auto',
                borderRadius: 'sm',
                '& > table': { borderCollapse: 'collapse', width: '100%' }
              }}
            >
              <Table stickyHeader sx={{ minWidth: 880 }}>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}></th>
                    <th style={{ width: 120 }}>Entry #</th>
                    <th style={{ width: 100 }}>Date</th>
                    <th style={{ width: 100 }}>Type</th>
                    <th>Description</th>
                    <th style={{ width: 120, textAlign: 'right' }}>Debits</th>
                    <th style={{ width: 120, textAlign: 'right' }}>Credits</th>
                    <th style={{ width: 100 }}>Status</th>
                    <th style={{ width: 80 }}>Balanced</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((entry) => (
                    <React.Fragment key={entry.id}>
                      <tr
                        style={{ cursor: 'pointer', backgroundColor: expandedRows.has(entry.id) ? '#f5f5f5' : 'transparent' }}
                        onClick={() => handleToggleRow(entry.id)}
                      >
                        <td>
                          <IconButton size="sm" variant="plain">
                            <ExpandMore
                              style={{
                                transform: expandedRows.has(entry.id) ? 'rotate(180deg)' : 'none',
                                transition: 'transform 0.2s'
                              }}
                            />
                          </IconButton>
                        </td>
                        <td>{entry.entry_number}</td>
                        <td>{formatDate(entry.entry_date)}</td>
                        <td>
                          <Chip size="sm" variant="soft">
                            {entry.entry_type}
                          </Chip>
                        </td>
                        <td>{entry.memo || entry.description || '-'}</td>
                        <td style={{ textAlign: 'right' }}>
                          {formatCurrency(entry.total_debits || 0)}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {formatCurrency(entry.total_credits || 0)}
                        </td>
                        <td>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={
                              entry.status === 'POSTED' ? 'success' :
                              entry.status === 'DRAFT' ? 'warning' :
                              'danger'
                            }
                          >
                            {entry.status}
                          </Chip>
                        </td>
                        <td>
                          {entry.is_balanced ? (
                            <CheckCircle size={20} color="var(--joy-palette-success-500)" />
                          ) : (
                            <Warning size={20} color="var(--joy-palette-warning-500)" />
                          )}
                        </td>
                      </tr>
                      {expandedRows.has(entry.id) && (
                        <tr>
                          <td colSpan={9} style={{ padding: 0 }}>
                            <Box sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                              {/* Entry Details */}
                              <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid xs={12} md={3}>
                                  <Typography level="body-sm" sx={{ fontWeight: 'bold' }}>
                                    Reference Number:
                                  </Typography>
                                  <Typography level="body-sm">
                                    {entry.reference_number || 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid xs={12} md={3}>
                                  <Typography level="body-sm" sx={{ fontWeight: 'bold' }}>
                                    Created By:
                                  </Typography>
                                  <Typography level="body-sm">
                                    {entry.created_by_name || 'System'}
                                  </Typography>
                                </Grid>
                                {entry.approved_by_name && (
                                  <Grid xs={12} md={3}>
                                    <Typography level="body-sm" sx={{ fontWeight: 'bold' }}>
                                      Approved By:
                                    </Typography>
                                    <Typography level="body-sm">
                                      {entry.approved_by_name}
                                    </Typography>
                                  </Grid>
                                )}
                                {entry.voided_by && (
                                  <Grid xs={12} md={3}>
                                    <Typography level="body-sm" sx={{ fontWeight: 'bold' }}>
                                      Void Reason:
                                    </Typography>
                                    <Typography level="body-sm">
                                      {entry.void_reason || 'N/A'}
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>

                              {/* Line Items */}
                              <Typography level="body-sm" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Line Items:
                              </Typography>
                              <Table size="sm">
                                <thead>
                                  <tr>
                                    <th>Account</th>
                                    <th>Description</th>
                                    <th style={{ textAlign: 'right' }}>Debit</th>
                                    <th style={{ textAlign: 'right' }}>Credit</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {entry.lines?.map((line, idx) => (
                                    <tr key={idx}>
                                      <td>
                                        {line.account_number} - {line.account_name}
                                      </td>
                                      <td>{line.description || '-'}</td>
                                      <td style={{ textAlign: 'right' }}>
                                        {line.debit_amount > 0 ? formatCurrency(line.debit_amount) : '-'}
                                      </td>
                                      <td style={{ textAlign: 'right' }}>
                                        {line.credit_amount > 0 ? formatCurrency(line.credit_amount) : '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </Box>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </Sheet>

            {/* Pagination Controls */}
            {data.count > 0 && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                {/* Page Size Selector */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography level="body-sm">Show:</Typography>
                  <Select
                    size="sm"
                    value={pageSize}
                    onChange={(_, value) => {
                      if (value) {
                        setPageSize(value);
                        setPage(1);
                      }
                    }}
                  >
                    <Option value={10}>10</Option>
                    <Option value={20}>20</Option>
                    <Option value={50}>50</Option>
                    <Option value={100}>100</Option>
                    <Option value={-1}>All</Option>
                  </Select>
                  <Typography level="body-sm" sx={{ ml: 2 }}>
                    {pageSize === -1
                      ? `Showing all ${data.count} entries`
                      : `Showing ${Math.min((page - 1) * pageSize + 1, data.count)}-${Math.min(page * pageSize, data.count)} of ${data.count} entries`
                    }
                  </Typography>
                </Box>

                {/* Navigation Controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    size="sm"
                    variant="outlined"
                    disabled={page === 1}
                    onClick={() => setPage(1)}
                  >
                    <FirstPage />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="outlined"
                    disabled={!data.previous}
                    onClick={() => setPage(page - 1)}
                  >
                    <NavigateBefore />
                  </IconButton>

                  {/* Page Numbers */}
                  {(() => {
                    const totalPages = Math.ceil(data.count / (pageSize === -1 ? 1000 : pageSize));
                    return Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }

                      if (pageNum < 1 || pageNum > totalPages) return null;

                      return (
                        <Button
                          key={pageNum}
                          size="sm"
                          variant={pageNum === page ? 'solid' : 'outlined'}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </Button>
                      );
                    });
                  })()}

                  <IconButton
                    size="sm"
                    variant="outlined"
                    disabled={!data.next || pageSize === -1}
                    onClick={() => setPage(page + 1)}
                  >
                    <NavigateNext />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="outlined"
                    disabled={!data.next || pageSize === -1}
                    onClick={() => setPage(Math.ceil(data.count / (pageSize === -1 ? 1000 : pageSize)))}
                  >
                    <LastPage />
                  </IconButton>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {!loading && data && data.results.length === 0 && (
        <Alert color="neutral">
          No journal entries found for the selected criteria.
        </Alert>
      )}
    </Box>
  );
};
